import React, { useState } from "react";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { useForm } from "react-hook-form";

import { ReactComponent as IconCross } from "../../../../shared/assets/cross.svg";
import { ReactComponent as IconCheck } from "../../../../shared/assets/check.svg";
import FormInput from "@ui-components/FormInput/FormInput";

import { useAuth0 } from "@auth0/auth0-react";
import { useAppDispatch } from "@redux/hooks";
import { updateOrder } from "@redux/slices/kontrolEdge/orderSlice";
import { fetchLastMileApi } from "../requests/request";

interface Address {
	street: string;
	city: string;
	state: string;
	country: string;
	postalCode: string;
}

interface LastMileConfigurationProps {
	address: Address;
	onAddressChange: (updatedAddress: Address) => void;
}

const LastMileConfiguration: React.FC<LastMileConfigurationProps> = ({
	address,
	onAddressChange,
}) => {
	const [coverageStatus, setCoverageStatus] = useState<
		"available" | "unavailable" | null
	>(null);
	const [estimatedPrice, setEstimatedPrice] = useState<number | null>(200);
	const [isLoading, setIsLoading] = useState(false);
	const dispatch = useAppDispatch();

	// React Hook Form setup for internal validation
	const { control, trigger, getValues } = useForm<Address>({
		mode: "onSubmit",
		defaultValues: address,
	});

	// Validate Coverage only if inputs are valid
	const handleValidateCoverage = async () => {
		const isValid = await trigger();
		if (!isValid) return;

		const formData = getValues();
		onAddressChange(formData);
		validateCoverage();
	};
	const { getAccessTokenSilently } = useAuth0();
	// Handle Coverage Validation API Call
	const validateCoverage = async () => {
		setIsLoading(true);
		try {
			const formData = getValues();
			const zipCode = formData.postalCode;
			const token = await getAccessTokenSilently();

			const response = await fetchLastMileApi(token, zipCode);
			const { coverage } = response.data;
			const { price } = response.data.price;
			// Dynamically update coverage status and estimated price
			if (coverage) {
				setCoverageStatus("available");
				setEstimatedPrice(price);
				dispatch(updateOrder({
				portIndex: 0, // or your current port index
				portData: {
				configuration: {
					crossConnectPricing: String(price),
				},
				},
			}));
			} else {
			setCoverageStatus("unavailable");
			setEstimatedPrice(null);
			dispatch(updateOrder({
				portIndex: 0,
				portData: {
				configuration: {
					crossConnectPricing: "",
				},
				},
			}));
			}
		} catch (error) {
			console.error("Error validating coverage:", error);
			setCoverageStatus("unavailable");
			setEstimatedPrice(null);
			dispatch(updateOrder({
			portIndex: 0,
			portData: {
				configuration: {
				crossConnectPricing: "",
				},
			},
			}));
		} finally {
			setIsLoading(false);
		}
		};

	return (
		<div className="mt-5 border rounded-sm py-5 px-3 bg-semantic-bg-primary">
			<FormInput
				name="country"
				control={control}
				label="Country"
				placeholder="Enter Country"
				required
			/>
			<FormInput
				name="postalCode"
				control={control}
				label="Postal Code"
				placeholder="Enter Postal Code"
				required
			/>
			<FormInput
				name="state"
				control={control}
				label="State"
				placeholder="Enter State"
				required
			/>
			<FormInput
				name="city"
				control={control}
				label="City"
				placeholder="Enter City"
				required
			/>
			<FormInput
				name="street"
				control={control}
				label="Street Address"
				placeholder="Enter Street"
				required
			/>

			{/* Validate Coverage Button */}
			<div className="flex justify-end mt-4">
				<GenericButton
					label={isLoading ? "Validating..." : "Validate Coverage"}
					variant="secondary"
					onClick={handleValidateCoverage}
					disabled={isLoading}
				/>
			</div>
			{/* Coverage Status Tags */}
			{coverageStatus && (
				<div className=" flex items-center justify-between mt-4">
					{coverageStatus === "available" ? (
						<>
							<GenericTag
								label={"Last Mile Available"}
								variant="success"
								icon={<IconCheck className="h-5 w-5" />}
							/>
							<p className="text-sm text-semantic-text-primary">
								Estimated Price: ${`${estimatedPrice}`} / month
							</p>
						</>
					) : (
						<div className="flex-wrap w-full">
							<div className="flex justify-between">
								<GenericTag
									label="Last Mile isn't available for this address. Only Cross-Connect accepted."
									variant="error"
									icon={<IconCross className="h-5 w-5" />}
								/>
								<p className="text-sm text-semantic-text-primary">
									Estimated Price: $ {estimatedPrice}{" "}
								</p>
							</div>
							<a className="flex mt-2 text-sm font-medium text-semantic-text-brand justify-end">Request custom price for last mile</a>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default LastMileConfiguration;
